import type { WorldDraft } from '../../../domain/types.js';
import { createSeededRng } from '../../../domain/seededRandom.js';
import type { CharacterOptionsOutput } from '../../../prompts/schemas.js';

export const TOP_STYLES = [
  'shirt', 'blouse', 't-shirt', 'sweater', 'jacket', 'apron', 'uniform', 'vest', 'tunic', 'coat',
] as const;

export const BOTTOM_STYLES = [
  'trousers', 'jeans', 'skirt', 'shorts', 'slacks', 'overalls', 'leggings', 'kilt', 'dress pants',
] as const;

export const HAIR_STYLES = [
  'short', 'bob', 'long', 'curly', 'braids', 'bun', 'ponytail', 'buzz', 'wavy', 'pixie',
] as const;

export const DOG_FUR_STYLES = [
  'short coat', 'long coat', 'curly coat', 'wire coat', 'double coat', 'smooth coat', 'fluffy', 'spotted',
] as const;

export const DOG_TOP_STYLES = [
  'harness', 'service vest', 'raincoat', 'bandana', 'collar only', 'working vest',
] as const;

export const DOG_BOTTOM_STYLES = [
  'none', 'booties', 'leg wraps',
] as const;

export const SKIN_SWATCHES = ['#f1c27d', '#e0ac69', '#8d5524', '#ffdbac', '#c68642', '#5c3d2e'];
export const COLOR_SWATCHES = ['#4cc9f0', '#f72585', '#80ed99', '#ff9e00', '#3a506b', '#22223b', '#b5179e', '#283618'];

export type AppearanceCatalogSource = 'frozen' | 'ai' | 'seeded';

export interface AppearanceCatalog {
  topStyles: readonly string[];
  bottomStyles: readonly string[];
  hairStyles: readonly string[];
  skinColors: readonly string[];
  colorSwatches: readonly string[];
  /** True when hairStyles holds fur/coat types instead of human haircuts. */
  usesFurSlot?: boolean;
}

function pick<T>(items: readonly T[], rng: () => number): T {
  return items[Math.floor(rng() * items.length)]!;
}

function slugPart(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'unknown';
}

function speciesKind(species: string): 'human' | 'dog' | 'other' {
  const s = species.trim().toLowerCase();
  if (s === 'human' || s === 'humans') return 'human';
  if (s === 'dog' || s === 'dogs' || s === 'canine') return 'dog';
  return 'other';
}

/** Sex is a key suffix. It is not the species segment. */
function speciesTokenFromKey(key: string): string {
  const parts = key.split('/').filter((part) => part.length > 0);
  const last = parts[parts.length - 1] ?? 'human';
  if (last === 'm' || last === 'f' || last === 'M' || last === 'F') return parts[parts.length - 2] ?? 'human';
  return last;
}

function sexFromKey(key: string): 'M' | 'F' | null {
  const last = key.split('/').filter((part) => part.length > 0).pop();
  if (last === 'm' || last === 'M') return 'M';
  if (last === 'f' || last === 'F') return 'F';
  return null;
}

const MALE_TOPS = ['shirt', 't-shirt', 'sweater', 'jacket', 'uniform', 'vest', 'tunic', 'coat'] as const;
const FEMALE_TOPS = ['blouse', 'shirt', 'sweater', 'jacket', 'vest', 'tunic', 'coat', 'apron'] as const;
const MALE_BOTTOMS = ['trousers', 'jeans', 'shorts', 'slacks', 'overalls', 'dress pants'] as const;
const FEMALE_BOTTOMS = ['trousers', 'jeans', 'skirt', 'shorts', 'leggings', 'slacks'] as const;

function periodLabel(draft: WorldDraft): string {
  const tp = draft.timePeriod;
  if (!tp) return 'present-day';
  return tp.period?.trim() || tp.present?.trim() || 'present-day';
}

/** Cache key = region + period + species (e.g. south-asia/present-day/dog). */
export function appearanceCatalogKey(
  region: string,
  period: string,
  species: string,
): string {
  return `${slugPart(region)}/${slugPart(period)}/${slugPart(species)}`;
}

export function appearanceCatalogKeyForDraft(
  draft: WorldDraft,
  species: string,
  sex?: 'M' | 'F',
): string {
  const region = draft.location?.region ?? 'unknown-region';
  const base = appearanceCatalogKey(region, periodLabel(draft), species);
  return sex ? `${base}/${sex}` : base;
}

function slice<T>(all: readonly T[], count: number, rng: () => number): T[] {
  return [...all].sort(() => rng() - 0.5).slice(0, count);
}

/** Deterministic species-aware seeded catalog — offline / no-key fallback. */
export function seededAppearanceCatalog(key: string, species?: string): AppearanceCatalog {
  const rng = createSeededRng(`appearance-catalog:${key}`);
  const kind = speciesKind(species ?? speciesTokenFromKey(key));

  if (kind === 'dog') {
    return {
      topStyles: slice(DOG_TOP_STYLES, 5, rng),
      bottomStyles: slice(DOG_BOTTOM_STYLES, 3, rng),
      hairStyles: slice(DOG_FUR_STYLES, 7, rng),
      skinColors: ['#c68642', '#8d5524', '#e0ac69', '#5c3d2e', '#f1c27d', '#22223b'],
      colorSwatches: ['#4a3728', '#f5f5dc', '#2f1810', '#808080', '#ffffff', '#1a1a1a', '#d4a574'],
      usesFurSlot: true,
    };
  }

  if (kind === 'other') {
    return {
      topStyles: slice(['shell', 'carapace', 'membrane', 'scales', 'none'], 4, rng),
      bottomStyles: slice(['tail fin', 'none', 'lower membrane'], 3, rng),
      hairStyles: slice(['tentacles', 'antennae', 'crest', 'frills', 'smooth'], 5, rng),
      skinColors: slice(SKIN_SWATCHES, 6, rng),
      colorSwatches: slice(COLOR_SWATCHES, 8, rng),
      usesFurSlot: true,
    };
  }

  const sex = sexFromKey(key);
  const tops = sex === 'M' ? MALE_TOPS : sex === 'F' ? FEMALE_TOPS : TOP_STYLES;
  const bottoms = sex === 'M' ? MALE_BOTTOMS : sex === 'F' ? FEMALE_BOTTOMS : BOTTOM_STYLES;
  return {
    topStyles: slice(tops, Math.min(8, tops.length), rng),
    bottomStyles: slice(bottoms, Math.min(7, bottoms.length), rng),
    hairStyles: slice(HAIR_STYLES, 8, rng),
    skinColors: SKIN_SWATCHES,
    colorSwatches: COLOR_SWATCHES,
    usesFurSlot: false,
  };
}

const SLOT_TO_FIELD: Record<string, keyof Pick<AppearanceCatalog, 'topStyles' | 'bottomStyles' | 'hairStyles' | 'colorSwatches'>> = {
  top: 'topStyles',
  bottom: 'bottomStyles',
  hair: 'hairStyles',
  fur: 'hairStyles',
  color: 'colorSwatches',
  shoes: 'bottomStyles',
};

const CREATURE_LABELS = new Set([
  'shell', 'carapace', 'membrane', 'scales', 'tentacles', 'antennae', 'crest', 'frills', 'tail fin', 'lower membrane',
]);

/** Map validated character_options director output into dropdown catalogs. */
export function catalogFromDirectorOutput(
  output: CharacterOptionsOutput,
  species: string,
  sex?: 'M' | 'F',
): AppearanceCatalog {
  const baseKey = appearanceCatalogKey('fallback', 'fallback', species);
  const base = seededAppearanceCatalog(sex ? `${baseKey}/${sex}` : baseKey, species);
  const human = speciesKind(species) === 'human';
  const next: AppearanceCatalog = {
    topStyles: [...base.topStyles],
    bottomStyles: [...base.bottomStyles],
    hairStyles: [...base.hairStyles],
    skinColors: [...base.skinColors],
    colorSwatches: [...base.colorSwatches],
    usesFurSlot: base.usesFurSlot,
  };

  for (const option of output.appearanceOptions) {
    const field = SLOT_TO_FIELD[option.slot];
    if (!field) continue;
    const label = option.label.trim().toLowerCase();
    if (!label) continue;
    if (human && (option.slot === 'fur' || CREATURE_LABELS.has(label))) continue;
    const list = next[field] as string[];
    if (!list.includes(label)) list.push(label);
    if (option.slot === 'fur') next.usesFurSlot = true;
  }

  if (next.topStyles.length === 0) next.topStyles = [...base.topStyles];
  if (next.bottomStyles.length === 0) next.bottomStyles = [...base.bottomStyles];
  if (next.hairStyles.length === 0) next.hairStyles = [...base.hairStyles];
  if (next.colorSwatches.length === 0) next.colorSwatches = [...base.colorSwatches];

  return next;
}

/** Finite period+location-aware catalogs (deterministic seed from world draft). */
export function appearanceCatalogForDraft(
  draft: WorldDraft,
  species = 'Human',
  sex?: 'M' | 'F',
): AppearanceCatalog {
  const key = appearanceCatalogKeyForDraft(draft, species, sex);
  return seededAppearanceCatalog(key, species);
}

export function randomAppearancePick(
  draft: WorldDraft,
  species = 'Human',
): {
  skinColor: string;
  topStyle: string;
  topColor: string;
  bottomStyle: string;
  bottomColor: string;
  hairStyle: string;
  hairColor: string;
} {
  const catalog = appearanceCatalogForDraft(draft, species);
  const rng = createSeededRng(`${draft.draftId}:look:${species}:${Date.now()}`);
  return {
    skinColor: pick(catalog.skinColors, rng),
    topStyle: pick(catalog.topStyles, rng),
    topColor: pick(catalog.colorSwatches, rng),
    bottomStyle: pick(catalog.bottomStyles, rng),
    bottomColor: pick(catalog.colorSwatches, rng),
    hairStyle: pick(catalog.hairStyles, rng),
    hairColor: pick(catalog.colorSwatches, rng),
  };
}
